import type { EcosystemRecordSchema, LegacyEcosystemRecordSchema } from '@ballerine/common';
import { checkIsDomainOrUrl } from '@ballerine/common';
import { Building, ExternalLink, Globe } from 'lucide-react';
import React from 'react';
import type { Components } from 'react-markdown';
import ReactMarkdown from 'react-markdown';
import type { z } from 'zod';

import { TextWithNAFallback } from '@/components/atoms/TextWithNAFallback';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '@/components/molecules/Accordion';

type EcosystemCardListProps = {
  ecosystem?: Array<z.infer<typeof EcosystemRecordSchema>> | null;
};

export const EcosystemCardList: React.FC<EcosystemCardListProps> = ({ ecosystem }) => {
  if (!ecosystem || ecosystem.length === 0) {
    return (
      <div className="bg-card text-card-foreground rounded-lg border shadow-sm py-4 px-6">
        <TextWithNAFallback>No ecosystem detected for the website.</TextWithNAFallback>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {ecosystem.map((item, index) => {
        const relationship = getEcosystemRelationship(item);
        const details = getEcosystemDetails(item);
        const accordionValue = `item-${index}`;

        return (
          <div
            key={accordionValue}
            className="border border-slate-200 rounded-lg shadow-sm bg-white"
          >
            <Accordion type="single" collapsible className="w-full">
              <AccordionItem value={accordionValue} className="border-none">
                <AccordionTrigger className="px-4 py-2 hover:no-underline hover:bg-slate-50 rounded-lg data-[state=open]:rounded-b-none">
                  <div className="flex items-center gap-4 w-full text-left">
                    <div className="flex-shrink-0 flex items-center justify-center">
                      {getEntityIcon(item.domain)}
                    </div>
                    <div className="flex-1 min-w-0 flex flex-col gap-1">
                      <p className="text-sm font-medium break-words max-w-md">
                        {renderDomainOrEntity(item.domain)}
                      </p>
                      {relationship && (
                        <p className="text-xs text-slate-400 truncate max-w-xl">
                          Relationship - {relationship}
                        </p>
                      )}
                    </div>
                  </div>
                </AccordionTrigger>
                <AccordionContent className="pl-10 pr-6 pb-4 border-t border-slate-100 pt-4 grid gap-3">
                  <EcosystemDetails details={details} />
                </AccordionContent>
              </AccordionItem>
            </Accordion>
          </div>
        );
      })}
    </div>
  );
};

type EcosystemDetail = {
  label: string;
  value: string;
};

const addProtocolIfMissing = (url: string) => {
  if (url.startsWith('http://') || url.startsWith('https://')) {
    return url;
  }

  return `http://${url}`;
};

const getEntityIcon = (domain: string) => {
  if (checkIsDomainOrUrl(domain)) {
    return <Globe className="h-5 w-5 text-slate-400" />;
  }

  return <Building className="h-5 w-5 text-slate-400" />;
};

const renderDomainOrEntity = (domainOrEntity: string) => {
  if (checkIsDomainOrUrl(domainOrEntity)) {
    const domainWithProtocol = addProtocolIfMissing(domainOrEntity);

    return (
      <a
        href={domainWithProtocol}
        onClick={e => e.stopPropagation()}
        target="_blank"
        rel="noopener noreferrer"
        title={domainWithProtocol}
        className="hover:underline"
      >
        {domainOrEntity}
        <ExternalLink className="inline-block ml-1 mb-[2px] h-3 w-3 text-blue-600 hover:text-blue-800" />
      </a>
    );
  }

  return (
    <span className="text-slate-900 break-words" title={domainOrEntity}>
      {domainOrEntity}
    </span>
  );
};

const isLegacyEcosystemItem = (
  ecosystemItem: z.infer<typeof EcosystemRecordSchema>,
): ecosystemItem is z.infer<typeof LegacyEcosystemRecordSchema> => {
  return 'relatedNode' in ecosystemItem;
};

const getEcosystemRelationship = (item: z.infer<typeof EcosystemRecordSchema>) => {
  if (isLegacyEcosystemItem(item)) {
    return item.relatedNodeType;
  }

  return item.relationship;
};

const getEcosystemDetails = (item: z.infer<typeof EcosystemRecordSchema>): EcosystemDetail[] => {
  if (isLegacyEcosystemItem(item)) {
    return [{ label: item.relatedNodeType, value: item.relatedNode }];
  }

  return item.nodes.map(({ type, value }) => ({
    label: type,
    value,
  }));
};

const formatMarkdownContent = (value: string | null | undefined) =>
  (value ?? '').replace(/\n\s*-\s*/g, ' ');

const EcosystemDetails: React.FC<{ details: EcosystemDetail[] }> = ({ details }) => {
  if (details.length === 0) {
    return null;
  }

  return (
    <>
      {details.map(({ label, value }, index) => (
        <div
          key={`${index}-${label}`}
          className="grid grid-cols-[120px,1fr,auto] gap-4 items-start py-2 border-b border-slate-50 last:border-0 text-sm"
        >
          <TextWithNAFallback className="text-slate-400 break-words">{label}</TextWithNAFallback>
          <div className="break-words leading-relaxed">
            <ReactMarkdown components={markdownComponents}>
              {formatMarkdownContent(value)}
            </ReactMarkdown>
          </div>
        </div>
      ))}
    </>
  );
};

const markdownComponents: Components = {
  a: ({ node, ...props }) => (
    <a
      className="text-xs py-1 px-2 bg-indigo-50 text-neutral-400 rounded-full hover:underline inline-block mx-1"
      target="_blank"
      rel="noopener noreferrer"
      {...props}
    />
  ),
  strong: ({ node, ...props }) => <strong className="font-semibold" {...props} />,
  em: ({ node, ...props }) => <em {...props} />,
  li: ({ node, ...props }) => <li className="max-w-[60ch]" {...props} />,
};
